import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, Download, GitBranch, MessageSquare, Terminal } from "lucide-react";
import SummaryDiagramResults from "../components/SummaryDiagramResults";
import data from "../data/exampleResults.json";

const PDF_URL = process.env.REACT_APP_BACKEND_URL + "/api/docs/how-it-works.pdf";

function Section({ section }) {
  const { title, kind, items = [] } = section;
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]">
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-wider text-white">{title}</span>
        <span className="text-[10px] font-mono text-zinc-600 uppercase">{kind}</span>
      </div>
      <div className="p-3 space-y-2 text-[12.5px]">
        {kind === "plaque" && (
          <pre className="text-[10px] leading-snug font-mono text-emerald-300 bg-[#050505] border border-emerald-400/20 p-2 overflow-x-auto whitespace-pre">
            {items.map((it) => (typeof it === "string" ? it : it.detail || "")).join("\n")}
          </pre>
        )}
        {(kind === "list" || kind === "expand") &&
          items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-amber-400 font-mono">·</span>
              <div>
                {typeof it === "string" ? (
                  <span className="text-zinc-200">{it}</span>
                ) : (
                  <>
                    <span className="text-white font-mono mr-2">{it.label}</span>
                    <span className="text-zinc-400">{it.detail}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        {kind === "plain" &&
          items.map((it, i) => (
            <p key={i} className="text-zinc-200 leading-relaxed">
              {typeof it === "string" ? it : it.detail || ""}
            </p>
          ))}
      </div>
    </div>
  );
}

const PANES = [
  { key: "reply", label: "BOT_REPLY", icon: MessageSquare },
  { key: "diagram", label: "DIAGRAM", icon: GitBranch },
  { key: "pdf", label: "PDF_PREVIEW", icon: FileText },
];

export default function LiveResultsPage() {
  const ex = (data.examples || [])[0];
  const [focus, setFocus] = useState(null); // null = side-by-side; key = expanded
  if (!ex) return null;

  const paneClass = (k) =>
    "border border-[#27272A] bg-[#0A0A0A] flex flex-col " +
    (focus && focus !== k ? "hidden xl:hidden " : "") +
    (focus === k ? "xl:col-span-3 " : "");

  return (
    <div data-testid="live-results-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="live-results-back" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white">
            <ArrowLeft size={13} /> THE_MINDSTEIN
          </Link>
          <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-amber-300 hover:text-amber-200">
            <FileText size={13} /> How It Works PDF
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">
            LIVE_RESULT · REAL_OUTPUT · UNEDITED
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white">
            {ex.topic}
          </h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-3xl leading-relaxed">
            One prompt produced everything below: dual DOI-verified ranking
            plaques, grounded facts, cross-domain BRIDGE tags, TRL feasibility
            scores, hard numerical metrics, a multi-tier synthesis, a five-sentence
            Final Summary — plus an auto-generated Executive Summary PDF and a
            publication-style structural diagram, saved straight to the Research
            Library.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono text-emerald-200 border border-emerald-400/30 bg-emerald-500/[0.05] px-3 py-1.5">
            <ShieldCheck size={13} /> Top-tier journals only · every bridge verified by ranked sources · SHA-256 + HMAC stampable
          </div>
          <div data-testid="live-doi-integrity" className="mt-2 inline-flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-emerald-300 border border-emerald-400/40 bg-emerald-500/[0.05] px-3 py-1.5">
            <ShieldCheck size={12} /> DOI_INTEGRITY · 11/11 DOIs VERIFIED
            <span className="text-zinc-500">·</span> 0 DUPLICATES
            <span className="text-zinc-500">·</span> 0 MISMATCHES
            <span className="text-zinc-500">·</span> 0 UNCONFIRMED
          </div>
        </div>

        {/* headline */}
        <h2 data-testid="live-headline" className="text-lg md:text-lg font-mono text-white border-l-2 border-emerald-400 pl-3">
          This is a real MindStein output. <span className="text-emerald-300">Every source verified.</span>{" "}
          <span className="text-amber-300">Every connection grounded.</span>
        </h2>

        {/* the exact prompt that produced everything below */}
        <div data-testid="live-prompt-headline" className="border border-amber-400/40 bg-[#0E0E0E]">
          <div className="px-3 py-2 border-b border-amber-400/30 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]">
            <Terminal size={12} className="text-amber-300" />
            <span className="text-amber-300">USER_PROMPT</span>
            <span className="text-zinc-600">· the single prompt that produced everything below</span>
          </div>
          <p className="p-3 text-[13.5px] text-zinc-100 leading-relaxed font-mono">
            {ex.prompt}
          </p>
        </div>

        {/* slideshow — side-by-side panes */}
        <div className="flex items-center gap-2" data-testid="live-slideshow-tabs">
          {PANES.map((p) => {
            const Icon = p.icon;
            return (
              <button key={p.key} data-testid={`live-pane-tab-${p.key}`}
                      onClick={() => setFocus(focus === p.key ? null : p.key)}
                      className={"px-3 h-8 text-[10px] font-mono uppercase tracking-wider border flex items-center gap-1 " +
                        (focus === p.key ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-[#27272A] text-zinc-400 hover:text-white")}>
                <Icon size={11} /> {p.label}
              </button>
            );
          })}
          <span className="text-[10px] font-mono text-zinc-600 ml-2 hidden sm:inline">
            click a tab to expand · click again for side-by-side
          </span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3" data-testid="live-slideshow">
          <div data-testid="live-pane-reply" className={paneClass("reply")}>
            <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
              1 · BOT_REPLY (FULL OUTPUT)
            </div>
            <div className="p-2 space-y-2 overflow-auto" style={{ maxHeight: 560 }}>
              {ex.sections.map((s, i) => (
                <Section key={i} section={s} />
              ))}
            </div>
          </div>

          <div data-testid="live-pane-diagram" className={paneClass("diagram")}>
            <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">2 · STRUCTURAL_DIAGRAM</span>
              <a data-testid="live-download-svg" href={ex.svg} download className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400 hover:text-white">
                <Download size={11} /> SVG
              </a>
            </div>
            <div className="p-2 bg-white flex-1 flex items-start justify-center overflow-auto" style={{ maxHeight: 560 }}>
              <img src={ex.svg} alt="Structural synthesis diagram — grounded connections only" className="w-full object-contain" />
            </div>
          </div>

          <div data-testid="live-pane-pdf" className={paneClass("pdf")}>
            <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">3 · EXECUTIVE_SUMMARY_PDF</span>
              <a data-testid="live-download-pdf" href={ex.pdf} download className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300 hover:text-amber-200">
                <Download size={11} /> PDF
              </a>
            </div>
            <iframe title="Executive Summary PDF" src={ex.pdf} className="w-full bg-white border-0 flex-1" style={{ minHeight: 560 }} />
          </div>
        </div>

        {/* Summary and Diagram Results — exactly what users see under every bot run */}
        <div className="space-y-2">
          <SummaryDiagramResults
            exec={{ id: "demo", pdf_url: ex.pdf, svg_url: ex.svg }}
            topic={ex.topic}
            demo
            showSaveToDevice
          />
          <span className="block text-[10px] font-mono text-zinc-600">
            This exact "SUMMARY_AND_DIAGRAM_RESULTS" tab appears under every synthesis in your account —
            each run auto-saves its PDF + diagram to your Research Library, where "Save to Device" downloads both.
          </span>
        </div>

        <div className="border-t border-[#27272A] pt-6 flex flex-wrap gap-3">
          <Link to="/register" data-testid="live-results-cta" className="px-5 h-11 bg-amber-400 text-black font-mono uppercase text-[12px] tracking-wider flex items-center hover:bg-amber-300">
            Start the free trial →
          </Link>
          <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="px-5 h-11 border border-emerald-400/50 text-emerald-300 font-mono uppercase text-[12px] tracking-wider flex items-center gap-2 hover:bg-emerald-500/10">
            <FileText size={14} /> Read the full method (PDF)
          </a>
        </div>
      </main>
    </div>
  );
}
